import React, { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Col, Row, Badge, Alert, UncontrolledTooltip } from "reactstrap"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useQuery, useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import Select from "react-select"

import { PeopleSchema, VehicleSchema } from "./IncidentModalsSchema"
import SearchPlaces from "../../Material/SearchPlaces"
import AddRepresentativeModal from './AddRepresentativeModal'
import './Modals.css'
const fetchMakes = async () => {
    const res = await fetch("https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json")
    const data = await res.json()
    return data.Results.map(m => ({ value: m.Make_Name, label: m.Make_Name }))
}

const fetchModels = async (make) => {
    if (!make) return []
    const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/getmodelsformake/${encodeURIComponent(make)}?format=json`)
    const data = await res.json()
    return data.Results.map(m => ({ value: m.Model_Name, label: m.Model_Name }))
}
export const CreateIncidentParty = gql`
  mutation createIncidentParty($input: CreateIncidentPartyMutationInput!){
    createIncidentParty(input:$input){
      ok
      errors{
        field
        messages
      }
    }
  }
`


const INJURIES_ENUM_QUERY = gql`
  query GetInjuriesChoices {
    injuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

function VehicleModal({ isOpen, toggle, onSave, defaultValues }) {
    const {
        control, handleSubmit, formState: { errors }, reset, watch, setValue
    } = useForm({
        resolver: yupResolver(VehicleSchema),
        defaultValues: defaultValues ?? {
            vin: "",
            make: "",
            model: "",
            year: "",
            plateNumber: "",
            damageDescription: "",
            insuranceCompany: "",
            policyNumber: "",
        },
    })

    const make = watch('make')
    const [makeOptions, setMakeOptions] = useState([])
    const [modelOptions, setModelOptions] = useState([])
    const [loadingModels, setLoadingModels] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        reset(defaultValues ?? {
            vin: "",
            make: "",
            model: "",
            year: "",
            plateNumber: "",
            damageDescription: "",
            insuranceCompany: "",
            policyNumber: "",
        })
        fetchMakes().then(setMakeOptions)
        if (defaultValues && defaultValues.make) {
            setLoadingModels(true)
            fetchModels(defaultValues.make).then((models) => {
                setModelOptions(models)
                setLoadingModels(false)
            })
        }
    }, [isOpen, defaultValues, reset])

    useEffect(() => {
        if (!defaultValues || make !== defaultValues.make) {
            setValue('model', '')
        }
        if (make) {
            setLoadingModels(true)
            fetchModels(make).then((models) => {
                setModelOptions(models)
                setLoadingModels(false)
            })
        } else {
            setModelOptions([])
        }
    }, [make, setValue, defaultValues])

    const submit = (data) => { onSave(data); toggle() }

    return (
        <Modal className="add-vehicle-modal modal-dialog-centered" isOpen={isOpen}>
            <ModalHeader>{defaultValues ? "Edit Vehicle" : "Add Vehicle"}</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit(submit)}>
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="make">Make</Label>
                                <Controller
                                    name="make"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            inputId="make"
                                            options={makeOptions}
                                            placeholder="Select Make"
                                            value={makeOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            onBlur={field.onBlur}
                                            isClearable
                                            isLoading={makeOptions.length === 0}
                                        />
                                    )}
                                />
                                {errors.make && <div className="invalid-feedback d-block">{errors.make.message}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="model">Model</Label>
                                <Controller
                                    name="model"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            inputId="model"
                                            options={modelOptions}
                                            placeholder={make ? "Select Model" : "Select Make First"}
                                            value={modelOptions.find(opt => opt.value === field.value) || null}
                                            onChange={opt => field.onChange(opt ? opt.value : '')}
                                            onBlur={field.onBlur}
                                            isClearable
                                            isDisabled={!make}
                                            isLoading={loadingModels}
                                        />
                                    )}
                                />
                                {errors.model && <div className="invalid-feedback d-block">{errors.model.message}</div>}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="year">Year</Label>
                                <Controller
                                    name="year"
                                    control={control}
                                    render={({ field }) => (
                                        <Input id="year" placeholder="e.g. 2020" invalid={!!errors.year} {...field} />
                                    )}
                                />
                                {errors.year && <div className="invalid-feedback d-block">{errors.year.message}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="vin">VIN</Label>
                                <Controller
                                    name="vin"
                                    control={control}
                                    render={({ field }) => (
                                        <Input id="vin" placeholder="e.g. 1HGCM82633A123456" invalid={!!errors.vin} {...field} />
                                    )}
                                />
                                {errors.vin && <div className="invalid-feedback d-block">{errors.vin.message}</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="plateNumber">License Plate</Label>
                                <Controller
                                    name="plateNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <Input id="plateNumber" placeholder="e.g. ABC1234" invalid={!!errors.plateNumber} {...field} />
                                    )}
                                />
                                {errors.plateNumber && <div className="invalid-feedback d-block">{errors.plateNumber.message}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label for="policyNumber">Policy Number</Label>
                                <Controller
                                    name="policyNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <Input id="policyNumber" placeholder="e.g. P123456789" invalid={!!errors.policyNumber} {...field} />
                                    )}
                                />
                                {errors.policyNumber && <div className="invalid-feedback d-block">{errors.policyNumber.message}</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-3">
                                <Label for="insuranceCompany">Insurance Company</Label>
                                <Controller
                                    name="insuranceCompany"
                                    control={control}
                                    render={({ field }) => (
                                        <Input id="insuranceCompany" placeholder="e.g. State Farm" invalid={!!errors.insuranceCompany} {...field} />
                                    )}
                                />
                                {errors.insuranceCompany && <div className="invalid-feedback d-block">{errors.insuranceCompany.message}</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-3">
                                <Label for="damageDescription">Damage Description</Label>
                                <Controller
                                    name="damageDescription"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="textarea" id="damageDescription" placeholder="Describe any damage to the vehicle" invalid={!!errors.damageDescription} {...field} />
                                    )}
                                />
                                {errors.damageDescription && <div className="invalid-feedback d-block">{errors.damageDescription.message}</div>}
                            </FormGroup>
                        </Col>
                    </Row>


                    <button type="submit" className="d-none" />
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button color="primary" onClick={handleSubmit(submit)}>
                    {defaultValues ? "Save Changes" : "Add Vehicle"}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

function PeopleModal({ isOpen, toggle, onSave, defaultValues, role }) {
    const { data: injuriesData, loading: injuriesLoading } = useQuery(INJURIES_ENUM_QUERY)
    const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm({
        resolver: yupResolver(PeopleSchema),
        defaultValues: defaultValues ?? {
            role,
            name: "",
            phoneNumber: "",
            email: "",
            address: "",
            driverLicense: "",
            injuries: "",
            statement: "",
            isBusinessEntity: false,
            representativeId: null
        },
    })

    const [isRepresentativeModalOpen, setRepresentativeModalOpen] = useState(false)
    const [representativeOptions, setRepresentativeOptions] = useState([])
    const { data: representativesData, refetch: refetchRepresentatives } = useQuery(gql`
        query AllIncidentPartyRepresentatives {
            allIncidentPartyRepresentatives {
                edges {
                    node {
                        id
                        name
                        businessName
                    }
                }
            }
        }
    `)

    useEffect(() => {
        const options = representativesData?.allIncidentPartyRepresentatives?.edges?.map(edge => ({
            label: edge.node.businessName + " - " + edge.node.name,
            value: edge.node.id,
        })) || []
        setRepresentativeOptions(options)
    }, [representativesData])

    useEffect(() => {
        if (!isOpen) return
        if (defaultValues) {
            reset({ ...defaultValues, role })
        } else {
            reset({
                role,
                name: "",
                phoneNumber: "",
                email: "",
                address: "",
                driverLicense: "",
                injuries: "",
                statement: "",
                isBusinessEntity: false,
                representativeId: null
            })
        }
    }, [isOpen, defaultValues, role, reset])

    const submit = (data) => { onSave({ ...data, role: data.role ?? role }); toggle() }

    const injuryOptions = injuriesData?.injuriesChoices?.enumValues?.map(opt => ({
        value: opt.name,
        label: opt.description || opt.name
    })) || []
    const toggleRepresentativeModal = () => setRepresentativeModalOpen(!isRepresentativeModalOpen)

    const title = (role === "VEHICLE_DRIVER" || role === "UNAUTHORIZED_DRIVER") ? "Driver" : "Passenger"

    const handleNewRepresentative = (newRepresentative) => {
        const newOption = { label: newRepresentative.name, value: newRepresentative.id }
        setRepresentativeOptions((prevOptions) => {
            const exists = prevOptions.some(option => option.value === newOption.value)
            if (!exists) {
                return [...prevOptions, newOption]
            }
            return prevOptions
        })
        setValue("representativeId", newOption.value)
    }

    return (
        <>
            <Modal isOpen={isOpen} className="add-vehicle-modal modal-dialog-centered">
                <ModalHeader>{defaultValues ? `Edit ${title}` : `Add ${title}`}</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit(submit)}>
                        <FormGroup className="mb-3">
                            <Label htmlFor="role">Role</Label>
                            {(role === "VEHICLE_DRIVER" || role === "UNAUTHORIZED_DRIVER") ? (
                                <Controller
                                    name="role"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            inputId="role"
                                            classNamePrefix="react-select"
                                            options={[
                                                { value: "VEHICLE_DRIVER", label: "VEHICLE DRIVER" },
                                                { value: "UNAUTHORIZED_DRIVER", label: "UNAUTHORIZED DRIVER" },
                                            ]}
                                            isClearable={false}
                                            value={field.value ? { value: field.value, label: field.value.replace("_", " ") } : null}
                                            onChange={(opt) => field.onChange(opt ? opt.value : "")}
                                            onBlur={field.onBlur}
                                        />
                                    )}
                                />
                            ) : (
                                <Input type="text" id="role" disabled value={role.replace("_", " ")} />
                            )}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="name">Name</Label>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <Input id="name" placeholder="e.g. John Doe" invalid={!!errors.name} {...field} />
                                )}
                            />
                            {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="phoneNumber">Phone Number</Label>
                            <Controller
                                name="phoneNumber"
                                control={control}
                                render={({ field }) => (
                                    <Input id="phoneNumber" placeholder="e.g. +11234567890" invalid={!!errors.phoneNumber} {...field} />
                                )}
                            />
                            {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber.message}</div>}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="email">Email</Label>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <Input id="email" type="email" placeholder="e.g. john.doe@example.com" invalid={!!errors.email} {...field} />
                                )}
                            />
                            {errors.email && <div className="invalid-feedback d-block">{errors.email.message}</div>}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="driverLicense">Driver License</Label>
                            <Controller
                                name="driverLicense"
                                control={control}
                                render={({ field }) => (
                                    <Input id="driverLicense" placeholder="e.g. D1234567" invalid={!!errors.driverLicense} {...field} />
                                )}
                            />
                            {errors.driverLicense && <div className="invalid-feedback d-block">{errors.driverLicense.message}</div>}
                        </FormGroup>
                        <Row>
                            <Col>
                                <FormGroup className="mb-3">
                                    <Label for="representative">Representative</Label>
                                    <Controller
                                        name="representativeId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                {...field}
                                                options={representativeOptions}
                                                isClearable
                                                placeholder="Select Representative"
                                                onChange={(option) => field.onChange(option ? option.value : null)}
                                                value={representativeOptions.find(opt => opt.value === field.value) || null}
                                            />
                                        )}
                                    />
                                </FormGroup>
                            </Col>
                            <Col className="text-end" xs={1}>
                                <Label>&nbsp;</Label>
                                <Button color="primary" id="add-representative-button" onClick={toggleRepresentativeModal}><i className="fa fa-plus" style={{ color: "white" }} /></Button>
                                <UncontrolledTooltip target="add-representative-button" placement="top">Add Representative</UncontrolledTooltip>
                            </Col>
                        </Row>
                        <FormGroup className="mb-3">
                            <Label htmlFor="address">Address</Label>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <SearchPlaces
                                        value={field.value ?? ""}
                                        searchType="address"
                                        onSaveAction={(item) => field.onChange(item?.name ?? "")}
                                    />
                                )}
                            />
                            {errors.address && <div className="invalid-feedback d-block">{errors.address.message}</div>}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="injuries">Injuries</Label>
                            <Controller
                                name="injuries"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        id="injuries"
                                        isLoading={injuriesLoading}
                                        options={injuryOptions}
                                        placeholder="Select injury status"
                                        isClearable
                                        onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                        value={injuryOptions.find(option => option.value === field.value) || null}
                                    />
                                )}
                            />
                            {errors.injuries && <div className="invalid-feedback d-block">{errors.injuries.message}</div>}
                        </FormGroup>
                        <FormGroup check className="mb-3">
                            <Controller
                                name="isBusinessEntity"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="checkbox"
                                        id="isBusinessEntity"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                )}
                            />
                            <Label for="isBusinessEntity" check className="ms-2">Is Business Entity</Label>
                            {errors.isBusinessEntity && <div className="invalid-feedback d-block">{errors.isBusinessEntity.message}</div>}
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label htmlFor="statement">Statement</Label>
                            <Controller
                                name="statement"
                                control={control}
                                render={({ field }) => (
                                    <Input type="textarea" id="statement" placeholder="Enter statement" invalid={!!errors.statement} {...field} />
                                )}
                            />
                            {errors.statement && <div className="invalid-feedback d-block">{errors.statement.message}</div>}
                        </FormGroup>

                        <button type="submit" className="d-none" />
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                    <Button color="primary" onClick={handleSubmit(submit)}>
                        {defaultValues ? "Save Changes" : `Add ${title}`}
                    </Button>
                </ModalFooter>
            </Modal>

            <AddRepresentativeModal
                isOpen={isRepresentativeModalOpen}
                toggle={toggleRepresentativeModal}
                refetchRepresentatives={refetchRepresentatives}
                onCreate={handleNewRepresentative}
            />
        </>
    )
}

export default function VehiclePartyModal({ isOpen, toggle, incidentId, refetchQuery, onCreate }) {
    const [createIncidentParty] = useMutation(CreateIncidentParty)
    const [partyName, setPartyName] = useState("")
    const [vehicle, setVehicle] = useState(null)
    const [driver, setDriver] = useState(null)
    const [passengers, setPassengers] = useState([])

    const [vehicleOpen, setVehicleOpen] = useState(false)
    const [peopleOpen, setPeopleOpen] = useState(false)
    const [editingPassengerIndex, setEditingPassengerIndex] = useState(null)
    const [currentRole, setCurrentRole] = useState("VEHICLE_DRIVER")
    const [error, setError] = useState(null)

    const createParty = async () => {
        setError(null)
        if (!partyName.trim()) {
            setError("Party Name is required")
            return
        }

        const input = {
            partyType: "VEHICLE",
            partyName: partyName.trim(),
            partyVehicle: vehicle,
            partyPeople: [...(driver ? [driver] : []), ...passengers],
        }
        if (incidentId) {
            input["incidentId"] = incidentId
        }
        if (onCreate) {
            onCreate(input)
            return
        }
        try {
            const res = await createIncidentParty({ variables: { input } })
            if (res.data?.createIncidentParty?.ok) {
                if (refetchQuery) refetchQuery()
                toggle()
            } else if (res.data?.createIncidentParty?.errors[0].messages[0]) {
                setError(res.data?.createIncidentParty?.errors[0].messages[0])
            } else {
                setError(res.data?.createIncidentParty?.errors)
            }
        } catch (err) {
            setError('An unexpected error occurred. ' + err.message)
        }
    }

    const onSave = (data) => {
        if (["VEHICLE_DRIVER", "UNAUTHORIZED_DRIVER"].includes(data.role ?? currentRole)) {
            setDriver(data)
        } else {
            if (editingPassengerIndex != null) {
                const next = [...passengers]
                next[editingPassengerIndex] = data
                setPassengers(next)
            } else {
                setPassengers((prev) => [...prev, data])
            }
            setEditingPassengerIndex(null)
        }
    }

    const onDeletePassenger = (idx) => {
        const next = passengers.filter((_, i) => i !== idx)
        setPassengers(next)
    }

    return (
        <>
            <Modal className="add-vehicle-modal modal-dialog-centered" isOpen={isOpen} size="lg">
                <ModalHeader>
                    Create Vehicle Party<br />
                    <small className="text-muted">Add vehicle, driver, and passengers involved in the incident</small>
                </ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger" >{error}</Alert>}
                    <Form>
                        <div className="border rounded p-3 mb-3">
                            <FormGroup className="mb-3">
                                <Label htmlFor="partyName">Party Name *</Label>
                                <Input
                                    id="partyName"
                                    placeholder="Enter party name (e.g., 'John's Vehicle', 'ABC Company Fleet')"
                                    value={partyName}
                                    required
                                    onChange={(e) => setPartyName(e.target.value)}
                                />
                            </FormGroup>
                        </div>

                        <div className="border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between">
                                <strong>Vehicle Information</strong>
                                <div className="d-flex">
                                    {!vehicle ?
                                        <Button size="sm" onClick={() => setVehicleOpen(true)}>
                                            <i className="fa fa-car" aria-hidden="true"></i>&nbsp;&nbsp;Add Vehicle
                                        </Button>
                                        :
                                        <Button size="sm" onClick={() => setVehicleOpen(true)}>
                                            <i className="fa fa-pencil-square-o" aria-hidden="true"></i>&nbsp;&nbsp;Edit
                                        </Button>
                                    }
                                </div>
                            </div>
                            <div className="mt-2">
                                {vehicle ? (
                                    <>
                                        <p className="mb-0">
                                            {vehicle?.year || "--"} {vehicle?.make || "--"} {vehicle?.model || "--"}
                                        </p>
                                        <small className="text-muted">
                                            PLATE: {vehicle.plateNumber || "-"} | VIN: {vehicle.vin || "-"}
                                        </small>
                                    </>
                                ) : (
                                    <small className="text-muted">No vehicle details added</small>
                                )}
                            </div>
                        </div>

                        <div className="border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between">
                                <strong>Driver</strong>
                                {!driver ?
                                    <Button className="float-right" size="sm" onClick={() => { setCurrentRole("VEHICLE_DRIVER"); setPeopleOpen(true) }}>
                                        <i className="fa fa-user" aria-hidden="true"></i>&nbsp;&nbsp;Add Driver
                                    </Button>
                                    :
                                    <Button className="float-right" size="sm" onClick={() => { setCurrentRole(driver.role || "VEHICLE_DRIVER"); setPeopleOpen(true) }}>
                                        <i className="fa fa-pencil-square-o" aria-hidden="true"></i>&nbsp;&nbsp;Edit
                                    </Button>}
                            </div>
                            <div className="mt-2">
                                {driver ? (
                                    <>
                                        <p className="mb-0">{driver.name}</p>
                                        <small className="text-muted">
                                            Driver's License #: {driver.driverLicense || "-"}<br />
                                            Phone: {driver.phoneNumber || "--"} <br />
                                            Email: {driver.email || "-"}<br />
                                            Injuries: {driver.injuries || "--"}<br />
                                            Address: {driver.address || "--"}<br />
                                            Is Business Entity: {driver.isBusinessEntity ? "Yes" : "No"}<br />
                                            Statement: {driver.statement || "--"}<br />
                                        </small>
                                    </>
                                ) : (
                                    <small className="text-muted">No driver assigned</small>
                                )}
                            </div>
                        </div>

                        <div className="border rounded p-3 mb-2">
                            <div className="d-flex justify-content-between">
                                <strong>Passengers <Badge pill>{passengers.length}</Badge></strong>
                                <Button size="sm" className="float-right" onClick={() => { setCurrentRole("VEHICLE_PASSENGER"); setEditingPassengerIndex(null); setPeopleOpen(true) }}>
                                    <i className="fa fa-user-plus" aria-hidden="true"></i>&nbsp;&nbsp;Add Passenger
                                </Button>

                            </div>
                            <div className="mt-2" style={{ maxHeight: 240, overflowY: "auto" }}>
                                {passengers.length === 0 ? (
                                    <small className="text-muted">No passengers added</small>
                                ) : (
                                    <>
                                        {passengers.map((p, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-start py-2">
                                                <div>
                                                    <p className="mb-1">
                                                        {p.name}&nbsp;&nbsp;
                                                        <i className="fa fa-pencil-square-o" aria-hidden="true" onClick={() => { setCurrentRole("VEHICLE_PASSENGER"); setEditingPassengerIndex(idx); setPeopleOpen(true) }} style={{ cursor: "pointer" }} />
                                                        &nbsp;&nbsp;
                                                        <i className="fa fa-trash" aria-hidden="true" onClick={() => onDeletePassenger(idx)} style={{ color: "red", cursor: "pointer" }} />
                                                    </p>
                                                    <small className="text-muted d-block">
                                                        Driver's License #: {p.driverLicense || "-"}<br />
                                                        Phone: {p.phoneNumber || "--"}<br />
                                                        Email: {p.email || "-"}<br />
                                                        Injuries: {p.injuries || "--"}<br />
                                                        Address: {p.address || "--"}<br />
                                                        Statement: {p.statement || "--"}
                                                    </small>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                    <Button color="primary" onClick={createParty}>{onCreate ? "Add Vehicle Party" : "Create Vehicle Party"}</Button>
                </ModalFooter>
            </Modal>

            {vehicleOpen &&
                <VehicleModal
                    isOpen={vehicleOpen}
                    toggle={() => setVehicleOpen(false)}
                    defaultValues={vehicle}
                    onSave={setVehicle}
                />
            }

            {peopleOpen &&
                <PeopleModal
                    isOpen={peopleOpen}
                    toggle={() => { setPeopleOpen(false); setEditingPassengerIndex(null) }}
                    defaultValues={currentRole === "VEHICLE_DRIVER" || currentRole === "UNAUTHORIZED_DRIVER" ? driver : (editingPassengerIndex != null ? passengers[editingPassengerIndex] : null)}
                    role={currentRole}
                    onSave={(data) => onSave(data)}
                />
            }
        </>
    )
}
